import { openDB, type IDBPDatabase } from 'idb';

export interface OfflineTicket {
  pack_item_id: string;
  puzzle_hash: string;
  solution_hash: string;
  difficulty: number;
  issued_at: number;
  schema_version: number;
  owner_user_id: string | null;
  sync_allowed: boolean;
  signature: string;
}

export interface OfflinePuzzle {
  id?: number;
  puzzle: (number | null)[][];
  solution: number[][];
  difficulty: number;
  givens: number;
  ticket: OfflineTicket;
}

export interface PendingOfflineGame {
  id?: number;
  client_id?: string;
  puzzle: (number | null)[][];
  solution: number[][];
  difficulty: number;
  duration_seconds: number;
  mistakes: number;
  hints_used: number;
  completed_at: number;
  won: boolean;
  ticket: OfflineTicket;
  /** Oyun BAŞLARKEN giriş yapmış olan kullanıcı — anonimse null, hiçbir zaman sync edilmez. */
  owner_user_id: string | null;
}

export interface QuarantinedOfflineGame extends PendingOfflineGame {
  reason: string;
  quarantined_at: number;
}

export interface ActiveOfflineGame {
  puzzle: (number | null)[][];
  solution: number[][];
  board: (number | null)[][];
  notes: number[][][];
  difficulty: number;
  mistakes: number;
  hints_used: number;
  score: number;
  elapsed_seconds: number;
  started_at: number;
  ticket: OfflineTicket;
  owner_user_id: string | null;
}

const DB_NAME = 'sudoku_offline';
const DB_VERSION = 2;
const STORE_POOL = 'puzzle_pool';
const STORE_PENDING = 'pending_games';
const STORE_ACTIVE = 'active_offline_game';
const STORE_QUARANTINE = 'quarantined_games';

let _dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        if (!db.objectStoreNames.contains(STORE_POOL)) {
          db.createObjectStore(STORE_POOL, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORE_PENDING)) {
          db.createObjectStore(STORE_PENDING, { keyPath: 'id', autoIncrement: true });
        }
        if (oldVersion < 2) {
          const poolStore = transaction.objectStore(STORE_POOL);
          // v1'deki bulmacalarda imzalı bilet (ticket) yok — yeni sync sözleşmesiyle
          // uyumsuz. Havuz zaten sadece yerel bir önbellek; temizlenip poolManager
          // tarafından imzalı bulmacalarla otomatik yeniden doldurulur.
          poolStore.clear();
          // v1'deki bekleyen sonuçlarda da ticket/owner_user_id yok — yeni sync
          // sözleşmesi (imzalı bilet zorunlu) bunları hiçbir zaman kabul edemez,
          // client tarafındaki owner filtresi de (undefined !== userId) hiçbir
          // zaman eşleşmediği için sessizce sonsuza dek kuyrukta kalırlardı
          // (Codex bulgusu, doğrulandı). Kurtarılamaz oldukları için temizlenir.
          transaction.objectStore(STORE_PENDING).clear();
          if (!poolStore.indexNames.contains('by_difficulty')) {
            poolStore.createIndex('by_difficulty', 'difficulty');
          }
          if (!poolStore.indexNames.contains('by_pack_item')) {
            // Aynı sunucu-bileti (pack_item_id) havuza iki kez eklenmesin.
            poolStore.createIndex('by_pack_item', 'ticket.pack_item_id', { unique: true });
          }
          if (!db.objectStoreNames.contains(STORE_ACTIVE)) {
            db.createObjectStore(STORE_ACTIVE);
          }
          if (!db.objectStoreNames.contains(STORE_QUARANTINE)) {
            db.createObjectStore(STORE_QUARANTINE, { keyPath: 'id', autoIncrement: true });
          }
        }
      },
    });
  }
  return _dbPromise;
}

export async function poolSize(): Promise<number> {
  const db = await getDb();
  return db.count(STORE_POOL);
}

// Havuz sayımı/tüketimi sahibe (bilet imzalanırken belirlenen owner_user_id)
// göre yapılır — aksi halde A'nın doldurduğu havuz "dolu" görünüp B'nin (ya da
// anonimin) hiç kendi bileti indirememesine yol açıyordu (Codex bulgusu,
// doğrulandı: cihazda hesap değişince/A→B geçişinde havuz B için asla dolmuyordu).
export async function countByDifficulty(ownerUserId: string | null): Promise<Record<number, number>> {
  const db = await getDb();
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const tx = db.transaction(STORE_POOL, 'readonly');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    const p = cursor.value as OfflinePuzzle;
    if (counts[p.difficulty] !== undefined && p.ticket.owner_user_id === ownerUserId) {
      counts[p.difficulty]++;
    }
    cursor = await cursor.continue();
  }
  await tx.done;
  return counts;
}

export async function addToPool(puzzles: Omit<OfflinePuzzle, 'id'>[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_POOL, 'readwrite');
  for (const p of puzzles) {
    try {
      await tx.store.add(p);
    } catch {
      // Aynı pack_item_id zaten havuzda (unique index) — sessizce atla.
    }
  }
  await tx.done;
}

/** İstenen zorlukta VE mevcut sahibe ait bulmaca yoksa `null` döner —
 * başka bir zorluğa ya da başka bir kullanıcının biletine sessizce ikame etmez. */
export async function takeOfflinePuzzle(difficulty: number, ownerUserId: string | null): Promise<OfflinePuzzle | null> {
  const db = await getDb();
  const tx = db.transaction(STORE_POOL, 'readwrite');
  const index = tx.store.index('by_difficulty');
  let cursor = await index.openCursor(IDBKeyRange.only(difficulty));
  while (cursor && (cursor.value as OfflinePuzzle).ticket.owner_user_id !== ownerUserId) {
    cursor = await cursor.continue();
  }
  if (!cursor) {
    await tx.done;
    return null;
  }
  const match = cursor.value as OfflinePuzzle;
  await cursor.delete();
  await tx.done;
  return match;
}

// Tek sabit anahtar (`ACTIVE_KEY`) kullanmak, farklı hesaplar (ya da anonim↔hesap)
// aynı cihazda offline oynadığında birinin devam eden oyununun diğeri tarafından
// sessizce üzerine yazılmasına yol açıyordu (Codex bulgusu, doğrulandı). Artık
// anahtar sahibe (owner_user_id) göre ayrılıyor — her sahip kendi tek aktif
// oyununu korur, biri diğerini asla ezmez.
function activeKey(ownerUserId: string | null): string {
  return ownerUserId ?? '__anonymous__';
}

export async function getActiveGame(ownerUserId: string | null): Promise<ActiveOfflineGame | null> {
  const db = await getDb();
  const result = await db.get(STORE_ACTIVE, activeKey(ownerUserId));
  return (result as ActiveOfflineGame | undefined) ?? null;
}

export async function saveActiveGame(game: ActiveOfflineGame): Promise<void> {
  const db = await getDb();
  await db.put(STORE_ACTIVE, game, activeKey(game.owner_user_id));
}

export async function clearActiveGame(ownerUserId: string | null): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_ACTIVE, activeKey(ownerUserId));
}

/**
 * Offline oyun bitince (kazanma/kaybetme) aktif-oyun kaydını silip sonucu
 * bekleyen-kuyruğa TEK bir transaction'da taşır — ikisi de commit olur ya da
 * hiçbiri olmaz (yarım kalmış bir geçiş yüzünden veri kaybı olmasın).
 */
export async function finalizeActiveGame(pending: Omit<PendingOfflineGame, 'id'>): Promise<void> {
  const db = await getDb();
  const tx = db.transaction([STORE_ACTIVE, STORE_PENDING], 'readwrite');
  await tx.objectStore(STORE_ACTIVE).delete(activeKey(pending.owner_user_id));
  await tx.objectStore(STORE_PENDING).add({
    ...pending,
    client_id: pending.client_id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  });
  await tx.done;
}

export async function drainPendingGames(): Promise<PendingOfflineGame[]> {
  const db = await getDb();
  return db.getAll(STORE_PENDING) as Promise<PendingOfflineGame[]>;
}

export async function clearPendingGames(ids: number[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_PENDING, 'readwrite');
  for (const id of ids) {
    await tx.store.delete(id);
  }
  await tx.done;
}

export async function pendingGamesCount(): Promise<number> {
  const db = await getDb();
  return db.count(STORE_PENDING);
}

/** Sunucunun reddettiği bir senkron kaydını sessizce silmek yerine buraya taşır
 * — otomatik tekrar denenmez ama veri kaybolmaz, sebep (`reason`) tutulur.
 * pending_games'ten silme İLE karantinaya ekleme TEK transaction'da yapılır —
 * aksi halde arada bir hata olursa aynı kayıt bir sonraki sync'te tekrar
 * karantinaya kopyalanabilirdi (Codex bulgusu, doğrulandı). */
export async function quarantineGame(game: PendingOfflineGame, reason: string): Promise<void> {
  const db = await getDb();
  const { id, ...rest } = game;
  const tx = db.transaction([STORE_QUARANTINE, STORE_PENDING], 'readwrite');
  await tx.objectStore(STORE_QUARANTINE).add({
    ...rest,
    reason,
    quarantined_at: Date.now(),
  });
  if (id !== undefined) await tx.objectStore(STORE_PENDING).delete(id);
  await tx.done;
}
